import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createOrganization } from '../../api/organizations';
import api from '../../api/client';
import { getAllCantons, getCantonName } from '../../utils/cantonData';
import { getCommunesByCantonCode } from '../../utils/communeData';
import { useTranslation } from 'react-i18next';
import styles from './OnboardingWizard.module.css';

const STEPS = ['welcome', 'organization', 'invite', 'done'] as const;
type Step = (typeof STEPS)[number];

interface Props {
  onComplete: () => void;
}

export function OnboardingWizard({ onComplete }: Props) {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('welcome');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Organization form
  const [orgName, setOrgName] = useState('');
  const [orgCode, setOrgCode] = useState('');
  const [selectedCantonCode, setSelectedCantonCode] = useState('');
  const [selectedCommune, setSelectedCommune] = useState('');

  // Invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteEmails, setInviteEmails] = useState<string[]>([]);

  const stepIndex = STEPS.indexOf(step);

  const communes = getCommunesByCantonCode(selectedCantonCode);
  const hasDropdownCommunes = communes.length > 0;

  function addEmail() {
    const email = inviteEmail.trim();
    if (email && !inviteEmails.includes(email)) {
      setInviteEmails([...inviteEmails, email]);
      setInviteEmail('');
    }
  }

  function removeEmail(email: string) {
    setInviteEmails(inviteEmails.filter((e) => e !== email));
  }

  async function tryCreateOrg(data: Parameters<typeof createOrganization>[0]) {
    try {
      return await createOrganization(data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      // If 409 (already exists), fetch the existing org by code
      if (status === 409) {
        const { listOrganizations } = await import('../../api/organizations');
        const orgs = await listOrganizations('flat');
        const existing = orgs.find((o) => o.code === data.code);
        if (existing) return existing;
      }
      throw err;
    }
  }

  async function handleCreateOrg() {
    if (!orgName.trim() || !orgCode.trim()) return;
    setLoading(true);
    setError('');

    try {
      // Create federal org (or reuse existing)
      const federal = await tryCreateOrg({
        name: orgName,
        code: orgCode,
        level: 'federal',
      });

      // Optionally create canton
      if (selectedCantonCode) {
        const cantonName = getCantonName(selectedCantonCode, i18n.language);
        const canton = await tryCreateOrg({
          name: cantonName,
          code: selectedCantonCode,
          level: 'canton',
          parent_id: federal.id,
        });

        // Optionally create commune
        if (selectedCommune.trim()) {
          const communeName = selectedCommune.trim();
          const communeCode = `${selectedCantonCode}-${communeName}`;
          await tryCreateOrg({
            name: communeName,
            code: communeCode,
            level: 'commune',
            parent_id: canton.id,
          });
        }
      }

      setStep('invite');
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || t('failedToCreateOrg'));
    } finally {
      setLoading(false);
    }
  }

  async function handleSendInvites() {
    setLoading(true);
    setError('');

    try {
      for (const email of inviteEmails) {
        await api.post('/invitations', { email, role: 'analyst' });
      }
      setStep('done');
    } catch {
      setError(t('someInvitationsFailed'));
      setStep('done');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.wizard}>
        {/* Progress bar */}
        <div className={styles.steps}>
          {STEPS.map((s, i) => (
            <div
              key={s}
              className={`${styles.step} ${i === stepIndex ? styles.stepActive : ''} ${i < stepIndex ? styles.stepDone : ''}`}
            />
          ))}
        </div>

        {error && <div className={styles.error}>{error}</div>}

        {/* Dismiss link — always visible except on the done step */}
        {step !== 'done' && (
          <button
            className={styles.dismissBtn}
            onClick={() => { onComplete(); navigate('/app'); }}
          >
            {t('skipSetup')}
          </button>
        )}

        {/* Step: Welcome */}
        {step === 'welcome' && (
          <>
            <h2 className={styles.title}>{t('welcomeTitle')}</h2>
            <p className={styles.subtitle}>
              {t('welcomeDescription')}
            </p>
            <div className={styles.actions}>
              <button className={styles.nextBtn} onClick={() => setStep('organization')}>
                {t('getStarted')}
              </button>
            </div>
          </>
        )}

        {/* Step: Create Organization */}
        {step === 'organization' && (
          <>
            <h2 className={styles.title}>{t('orgSetup')}</h2>
            <p className={styles.subtitle}>
              {t('orgSetupDescription')}
            </p>

            <div className={styles.field}>
              <label>{t('common:federalOrg')}</label>
              <div className={styles.row}>
                <input
                  placeholder={t('nameExample')}
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                />
                <input
                  placeholder={t('codeExample')}
                  value={orgCode}
                  onChange={(e) => setOrgCode(e.target.value)}
                />
              </div>
            </div>

            <div className={styles.field}>
              <label>{t('cantonOptional')}</label>
              <select
                value={selectedCantonCode}
                onChange={(e) => {
                  setSelectedCantonCode(e.target.value);
                  setSelectedCommune('');
                }}
              >
                <option value="">{t('common:selectCanton')}</option>
                {getAllCantons().map((c) => (
                  <option key={c.code} value={c.code}>
                    {getCantonName(c.code, i18n.language)} ({c.code})
                  </option>
                ))}
              </select>
            </div>

            {selectedCantonCode && (
              <div className={styles.field}>
                <label>{t('communeOptional')}</label>
                {hasDropdownCommunes ? (
                  <select
                    value={selectedCommune}
                    onChange={(e) => setSelectedCommune(e.target.value)}
                  >
                    <option value="">{t('common:selectCommune')}</option>
                    {communes.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    placeholder="Commune name"
                    value={selectedCommune}
                    onChange={(e) => setSelectedCommune(e.target.value)}
                  />
                )}
                {selectedCommune && (
                  <span className={styles.codeHint}>
                    {t('codeLabel', { cantonCode: selectedCantonCode, commune: selectedCommune })}
                  </span>
                )}
              </div>
            )}

            <div className={styles.actions}>
              <button className={styles.backBtn} onClick={() => setStep('welcome')}>
                {t('common:back')}
              </button>
              <button
                className={styles.nextBtn}
                onClick={handleCreateOrg}
                disabled={loading || !orgName.trim() || !orgCode.trim()}
              >
                {loading ? t('creating') : t('createAndContinue')}
              </button>
            </div>
          </>
        )}

        {/* Step: Invite Team */}
        {step === 'invite' && (
          <>
            <h2 className={styles.title}>{t('inviteTeam')}</h2>
            <p className={styles.subtitle}>
              {t('inviteTeamDescription')}
            </p>

            <div className={styles.inviteRow}>
              <input
                type="email"
                placeholder={t('addEmailPlaceholder')}
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addEmail())}
              />
              <button className={styles.addBtn} onClick={addEmail}>
                {t('common:add')}
              </button>
            </div>

            {inviteEmails.length > 0 && (
              <ul className={styles.emailList}>
                {inviteEmails.map((email) => (
                  <li key={email}>
                    <span>{email}</span>
                    <button className={styles.removeBtn} onClick={() => removeEmail(email)}>
                      {t('common:remove')}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className={styles.actions}>
              <button className={styles.backBtn} onClick={() => setStep('done')}>
                {t('common:skip')}
              </button>
              <button
                className={styles.nextBtn}
                onClick={handleSendInvites}
                disabled={loading || inviteEmails.length === 0}
              >
                {loading ? t('sending') : t('sendInvitations', { count: inviteEmails.length })}
              </button>
            </div>
          </>
        )}

        {/* Step: Done */}
        {step === 'done' && (
          <>
            <div className={styles.doneIcon}>&#10003;</div>
            <h2 className={styles.title} style={{ textAlign: 'center' }}>{t('allSet')}</h2>
            <p className={styles.subtitle} style={{ textAlign: 'center' }}>
              {t('allSetDescription')}
            </p>
            <div className={styles.actions} style={{ justifyContent: 'center' }}>
              <button
                className={styles.nextBtn}
                onClick={() => {
                  onComplete();
                  navigate('/app');
                }}
              >
                {t('goToDashboard')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
